import React, { useState, useEffect } from 'react';
import { IS_PROF, MY_ID } from '../../../../helpers/user/authorization';
import { useMutation } from '@apollo/client';
import { UPDATE_USER_ACCOUNT } from '../../../../graphql/mutation/user/updateUserAccount';
import { AccountFields } from './AccountFields';
import { ChangePassword } from '../ChangePassword';
import { LoadingContainer } from 'components/molecules/Loaders/LoadingContainer';
import { HeaderText } from 'components/atoms/Texts/HeaderText';
import emojis from 'theme/emoji';
import { ButtonWithText } from 'components/atoms/Buttons/ButtonWithText';
import { UpdateUserAccountMutation } from '../../../../graphql/generated/graphql';
import { ProfileInfo } from '../../../../types/user/user.def';
import useStyle from './accountDetails.style';
import { FeatureContainer } from 'components/molecules/Containers/FeatureContainer';
import { QUERY_USER_HOME } from '../../../../graphql/query/user/queryUserHome';
import { QUERY_USER_ACCOUNT } from '../../../../graphql/query/user/queryUserAccount';
import toast from 'react-hot-toast';
import { accountDetailsFormErrorHandling } from '../../../../helpers/forms/formsErrorHandling/accountDetailsErrorHandling';
import { MESSAGES } from '../../../../helpers/messages';
import { useDispatch } from 'react-redux';
import { updateUser as updateUserAction } from '../../../../store/actions/user.actions';

interface AccountDetailsProps {
  user: ProfileInfo;
  editablePassword?: boolean;
  visibleTitle?: boolean;
}

export const AccountDetails = ({
  user,
  editablePassword = true,
  visibleTitle = true
}: AccountDetailsProps) => {
  const [updateUser] = useMutation<UpdateUserAccountMutation>(
    UPDATE_USER_ACCOUNT,
    {
      refetchQueries: [
        { query: QUERY_USER_HOME, variables: { id: user.id } },
        { query: QUERY_USER_ACCOUNT, variables: { id: user.id } }
      ]
    }
  );
  const classes = useStyle();
  const [formData, setFormData] = useState({
    values: user,
    errors: {},
    isValid: false
  });
  const dispatch = useDispatch();
  useEffect(() => {
    setFormData({ ...formData, values: { ...user } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!formData) return <LoadingContainer />;

  const handleChange = field => e => {
    const newParams = accountDetailsFormErrorHandling(
      formData,
      field,
      e.target.value
    );
    setFormData({
      ...formData,
      ...newParams
    });
  };

  const submit = () => {
    updateUser({
      variables: { id: user.id, ...formData.values }
    })
      .then(() => {
        toast.success('Modification apportée avec succès');
        dispatch(updateUserAction(formData.values));
      })
      .catch(err => {
        if (err.message === 'Email already taken') {
          toast.error(MESSAGES.error.mailExisteUpdate);
          return;
        }
        toast.error(err.message);
      });
  };
  return (
    <div className={classes.root}>
      {visibleTitle && (
        <HeaderText
          text={IS_PROF ? 'Modification de profil' : 'Détails de votre profil'}
          emoji={emojis['Crayon']}
        />
      )}
      <FeatureContainer>
        <div className={classes.form}>
          <div className={classes.headBox}>
            <h2>Nom d'utilisateur (non modifiable)</h2>
            <span>{user.username}</span>
          </div>
          <AccountFields
            errors={formData.errors}
            handleChange={handleChange}
            fields={formData.values}
          />
          <div>
            <ButtonWithText
              content="Sauvegarder les modifications"
              disabled={!formData.isValid}
              onClick={submit}
            />
          </div>
        </div>
      </FeatureContainer>
      {user.id === MY_ID && editablePassword && (
        <>
          <HeaderText
            text="Modifier mon mot de passe"
            emoji={emojis['Locked']}
          />
          <FeatureContainer>
            <ChangePassword />
          </FeatureContainer>
        </>
      )}
    </div>
  );
};
